import React, { useEffect, useState } from "react";
import { CTA02, DynamicNavigation, Footer, MeetOurTeam, FAQSection } from "./components";
import AboutJourneySection from "./components/AboutJourneySection";
import BreadcrumbNav from "./components/BreadcrumbNav";
import { useSeo } from "./seo";
import { useNavLinkClick } from "./hooks/useNavLinkClick";

const DESKTOP_BANNER_URL = "/assets/about-hero/about-banner.jpg";
const MOBILE_BANNER_URL = "/assets/about-hero/mobile/about-banner.jpg";
const FALLBACK_BANNER_URL = "/assets/services-hero/services-hero-desktop.jpg";

const FAQ_ITEMS = [
  {
    question: "I want to migrate to Australia but don't know where to begin. Can Hola World help?",
    answer: "Yes, starting your Australia migration journey can feel confusing with multiple visa options and requirements. Hola World helps you understand your eligibility, explore the best visa pathways, and guide you through documentation and applications so you can begin your move to Australia with clarity and confidence.",
  },
  {
    question: "Which visa should I choose if I want to settle in Australia permanently?",
    answer: "The right visa depends on your education, work experience, and long-term goals. Skilled migration visas like subclass 189, 190, and 491 are popular PR pathways. Hola World evaluates your profile and recommends the most suitable visa for permanent settlement in Australia.",
  },
  {
    question: "Is IELTS required for Australia migration or PR?",
    answer: "Most Australia visa pathways require proof of English proficiency. Tests like IELTS or PTE are commonly accepted for student visas, skilled migration, and permanent residency. The required score varies by visa category. Hola World helps you choose the right test and prepare for your target score.",
  },
  {
    question: "How long does the Australia migration process take?",
    answer: "Processing time depends on the visa type and application strength. Student visas may take a few weeks, while skilled migration or PR visas can take several months due to assessments and invitations. Hola World keeps you informed throughout the process so you can plan confidently.",
  },
  {
    question: "Can I migrate to Australia without a job offer?",
    answer: "Yes, some Australia migration pathways do not require a job offer, especially skilled visas based on points and occupation demand. However, having employment can improve your profile. Hola World reviews your eligibility and helps you choose the best pathway with higher approval chances.",
  },
  {
    question: "Can I get Australia PR after studying there?",
    answer: "Yes, studying in Australia can lead to permanent residency if you choose the right course and career pathway. Factors like occupation demand and work experience are important. Hola World helps students plan smart study routes that improve their chances of securing Australia PR.",
  },
];

export default function AboutPage() {
  useSeo({
    title: "About | Hola World Migration",
    description:
      "Learn about Hola World Migration, our process, and our team supporting migration and visa pathways to Australia.",
    path: "/about",
    // image property removed to force global OG image
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "About", url: "/about" },
    ],
    faqItems: FAQ_ITEMS,
  });

  const [bannerLoaded, setBannerLoaded] = useState(false);

  // Preload hero banners (desktop + mobile)
  useEffect(() => {
    let loaded = 0;
    const handleLoaded = () => {
      loaded += 1;
      if (loaded >= 2) {
        setBannerLoaded(true);
      }
    };

    [DESKTOP_BANNER_URL, MOBILE_BANNER_URL].forEach((src) => {
      const img = new Image();
      img.onload = handleLoaded;
      img.onerror = handleLoaded;
      img.src = src;
    });
  }, []);

  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "services", label: "Services", href: "/services" },
    { id: "about", label: "About", href: "/about" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];

  const onLinkClick = useNavLinkClick(links);

  return (
    <div className="min-h-screen bg-slate-100 overflow-x-hidden">
      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Logo" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
          activeLink="about"
        />
      </header>

      {/* Hero Section */}
      <section id="about" className="relative w-full">
        {/* Desktop Hero Banner */}
        <div className="hidden md:block w-full hero">
          <img
            src="/assets/about-hero/about-banner.jpg"
            alt="About banner"
            className="hero-img block select-none"
            loading="eager"
            decoding="async"
            draggable={false}
          />
          {/* Overlay Content - match Services layout */}
          <div className="absolute inset-0 z-10 flex items-center justify-center px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-start justify-center w-full max-w-6xl">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg leading-tight">
                Guiding Your Journey<br />
                to Australia with<br />
                Expertise
              </h1>
            </div>
          </div>
        </div>
        <style>{`.hero { border-bottom: 4px solid #fff !important; }`}</style>
        {/* Mobile Hero Banner */}
        <div className="block md:hidden w-full relative overflow-hidden" style={{ maxWidth: '100vw' }}>
          <div style={{ width: '100%', paddingTop: '150%', position: 'relative', background: 'transparent' }} aria-hidden="true">
            <img
              src={MOBILE_BANNER_URL}
              alt="About banner"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', background: 'transparent' }}
              srcSet={[
                `${MOBILE_BANNER_URL} 640w`,
                `${MOBILE_BANNER_URL.replace('.jpg', '@2x.jpg')} 1280w`,
                `${MOBILE_BANNER_URL.replace('.jpg', '@3x.jpg')} 1920w`
              ].join(', ')}
              sizes="100vw"
              loading="eager"
              decoding="async"
              className="block select-none"
              draggable={false}
              onLoad={() => setBannerLoaded(true)}
            />
            {/* Overlay Content */}
            <div className="absolute inset-0 z-10 flex items-center justify-center px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col items-center w-full max-w-2xl mx-auto">
                <h1 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">About Australia Migration</h1>
                <p className="mt-2 text-base text-white/90 max-w-2xl">
                  Hola World offers expert migration support for Australia, guiding you through PR pathways, visas, and relocation with a smooth and stress-free experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb Section */}
      <section className="relative">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 pt-8">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "About", href: "/about" },
            ]}
          />
        </div>
      </section>

      {/* About Content */}
      <main className="pb-6">
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-4xl">
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              We provide trusted migration guidance, visa pathway planning, and end-to-end support for individuals and families moving to Australia. With a team of qualified education agents and registered migration specialists, we make your journey smooth and stress-free.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our mission is to empower aspiring migrants with accurate information, professional guidance, and compassionate support every step of the way.
            </p>
          </div>
        </section>

        <AboutJourneySection />
        <MeetOurTeam />
        <FAQSection />
        <CTA02 />
      </main>

      <Footer />
    </div>
  );
}
